// The three families of wood. A family is one voice: how one tap sounds at a
// pitch, and how much air and creak it has. Every moment is written once with
// `tap`, so a moment changes its wood when it changes its family. Pure.

import { knock, type Layer, ring, sweep } from './parts';

export type SoundFamily = 'block' | 'log' | 'twig';

export type Voice = {
  /** One tap at `hz`. `long` scales how long it rings. */
  tap: (hz: number, at?: number, gain?: number, long?: number) => Layer[];
  /** The share of paper air, from 0 to 1. */
  air: number;
  /** The share of creak, from 0 to 1. */
  bend: number;
};

/** A second partial at this ratio to the note is wood. */
const WOOD_PARTIAL = 2.62;
/** A twig starts at this share of its note and bends up to it. */
const TWIG_BEND_FROM = 0.82;
/** Each tap at another moment gets another strike. */
const seedAt = (base: number, at: number): number => base + Math.round(at * 997);

export const VOICES: Record<SoundFamily, Voice> = {
  // Hard dry wood: a chess piece on a board, a temple block.
  block: {
    tap: (hz, at = 0, gain = 1, long = 1) => [
      ring(hz, at, gain, [
        [1, 1, 0.006 * long],
        [WOOD_PARTIAL, 0.3, 0.0027 * long],
      ]),
      knock(at, gain * 0.45, 0.0013, seedAt(1, at)),
    ],
    air: 0.6,
    bend: 0.3,
  },
  // A hollow log under a soft mallet: an octave lower, longer, with a body.
  log: {
    tap: (hz, at = 0, gain = 1, long = 1) => [
      ring(hz * 0.5, at, gain, [
        [1, 1, 0.045 * long],
        [2.76, 0.32, 0.016 * long],
        [5.4, 0.1, 0.006 * long],
      ]),
      ring(hz * 0.25, at, gain * 0.35, [[1, 1, 0.03 * long]]),
      knock(at, gain * 0.18, 0.003, seedAt(3, at)),
    ],
    air: 0.35,
    bend: 0.5,
  },
  // Twig and cork: a soft tone that bends to its note, with no strike.
  twig: {
    tap: (hz, at = 0, gain = 1, long = 1) => [
      sweep(hz * TWIG_BEND_FROM, hz, at, gain, {
        glide: 0.008 * long,
        halfLife: 0.014 * long,
        seconds: 0.1 * long,
      }),
      sweep(hz * WOOD_PARTIAL * TWIG_BEND_FROM, hz * WOOD_PARTIAL, at, gain * 0.16, {
        glide: 0.006 * long,
        halfLife: 0.005 * long,
        seconds: 0.04 * long,
      }),
    ],
    air: 1,
    bend: 1,
  },
};
